import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdTrendingUp,
  MdCheckCircle,
  MdHistory,
  MdWarningAmber,
  MdExpandMore,
  MdSchedule,
  MdFlag,
  MdMailOutline,
  MdBarChart,
  MdOpenInNew,
  MdReceiptLong,
  MdAccessTime,
  MdDeleteForever,
} from "react-icons/md";
import SkeletonTable from "../../../../components/skeleton/table";
import api from "../../../../services/axios";
import { usePermissions } from "../../../../auth/PermissionProvider";
import { Permissions } from "../../../../auth/permissions";
import ReportPreviewModal from "./ReportPreviewModal";
import ProjectReportModal from "./ProjectReportModal";
import BillingComposeModal from "./BillingComposeModal";
import InvoiceDocModal from "./InvoiceDocModal";
import { toast } from "react-toastify";
import {
  FinancialOverview as Overview,
  ClientOverview,
  ClientBillingRef,
  ProjectOverview,
  BillingTypeName,
} from "../../../../interfaces/Financial";

interface Props {
  month: number;
  year: number;
  /** Muda de valor quando uma cobrança é criada/alterada no pai → força recarregar a visão. */
  reloadSignal?: number;
}

const fmtMoney = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtHours = (h: number) =>
  `${h.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const billingTypeChip = (t: number | null) => {
  const map: Record<number, string> = {
    1: "bg-dv-magenta-50 text-dv-magenta-700 dark:bg-dv-magenta-900/40 dark:text-dv-magenta-300",
    2: "bg-dv-blue-50 text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-blue-300",
    3: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  };
  const cls = t ? map[t] : "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      {t ? BillingTypeName[t as 1 | 2 | 3] : "—"}
    </span>
  );
};

const FinancialOverview: React.FC<Props> = ({ month, year, reloadSignal }) => {
  const { can } = usePermissions();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [onlyPending, setOnlyPending] = useState(false);
  const [previewClient, setPreviewClient] = useState<{ id: number; name: string } | null>(null);
  const [projectReport, setProjectReport] = useState<{ id: number; name: string } | null>(null);
  const [composeClient, setComposeClient] = useState<ClientOverview | null>(null);
  const [loadingBillingId, setLoadingBillingId] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ClientBillingRef | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ClientBillingRef | null>(null);
  const [invoiceTarget, setInvoiceTarget] = useState<{ billing: ClientBillingRef; clientId: number; clientName: string } | null>(null);
  const navigate = useNavigate();

  const canDelete = can(Permissions.FinancialAdmin);

  // Só quem gerencia projetos vê essas ações; o time "Finance" fica só no módulo financeiro
  // (esconde a navegação para o cadastro de projeto — sem levar a uma tela sem acesso).
  const canManage = can(Permissions.ProjectsManage);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<Overview>("/Financial/Overview", { params: { month, year } });
      setData(res.data);
    } catch (err) {
      console.error("Error fetching financial overview:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOverview, reloadSignal]);

  const handleBillingAction = async (action: "emit" | "receive", billingId: number) => {
    const url = action === "emit"
      ? `/Financial/EmitInvoice/${billingId}`
      : `/Financial/MarkAsReceived/${billingId}`;
    const msg = action === "emit" ? "Nota fiscal emitida." : "Marcado como recebido.";
    setLoadingBillingId(billingId);
    try {
      await api.post(url);
      toast.success(msg, { position: "top-right", autoClose: 4000 });
      await fetchOverview();
    } catch (err: any) {
      const m = err?.response?.data;
      toast.error(typeof m === "string" ? m : "Operação falhou.", { position: "top-right", autoClose: 4000 });
    } finally {
      setLoadingBillingId(null);
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setLoadingBillingId(cancelTarget.billingId);
    try {
      await api.post(`/Financial/Cancel/${cancelTarget.billingId}`, { reason: cancelReason.trim() });
      toast.success("OS cancelada.", { position: "top-right", autoClose: 4000 });
      setCancelTarget(null);
      setCancelReason("");
      await fetchOverview();
    } catch (err: any) {
      const m = err?.response?.data;
      toast.error(typeof m === "string" ? m : "Falha ao cancelar.", { position: "top-right", autoClose: 4000 });
    } finally {
      setLoadingBillingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setLoadingBillingId(deleteTarget.billingId);
    try {
      await api.delete(`/Financial/Billings/${deleteTarget.billingId}`);
      toast.success("OS excluída.", { position: "top-right", autoClose: 4000 });
      setDeleteTarget(null);
      await fetchOverview();
    } catch (err: any) {
      const m = err?.response?.data;
      toast.error(typeof m === "string" ? m : "Falha ao excluir.", { position: "top-right", autoClose: 4000 });
    } finally {
      setLoadingBillingId(null);
    }
  };

  const toggle = (clientId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(clientId) ? next.delete(clientId) : next.add(clientId);
      return next;
    });

  const clients = useMemo(() => {
    if (!data) return [];
    return onlyPending ? data.clients.filter((c) => c.needsAttention) : data.clients;
  }, [data, onlyPending]);

  if (loading) return <SkeletonTable active={loading} />;
  if (!data) return null;

  const realizedPct = data.totalForecast > 0 ? Math.round((data.totalReal / data.totalForecast) * 100) : 0;

  return (
    <section className="animate-fade-up">
      {/* ── Hero / KPIs ─────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl bg-dv-gradient p-6 text-white shadow-dv-glow">
        <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
              Painel financeiro
            </p>
            <h2 className="text-xl font-extrabold capitalize">
              {MONTHS[month - 1]} {year}
            </h2>
          </div>
          <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80 ring-1 ring-white/15">
            {data.clients.length} clientes ativos
          </span>
        </div>

        <div className="relative mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiCard
            icon={<MdTrendingUp className="h-5 w-5" />}
            label="Previsto do mês"
            value={fmtMoney(data.totalForecast)}
            sub={`${fmtHours(data.totalHours)} apontadas`}
          />
          <KpiCard
            icon={<MdCheckCircle className="h-5 w-5" />}
            label="Realizado"
            value={fmtMoney(data.totalReal)}
            sub={`${realizedPct}% do previsto`}
            accent="emerald"
            progress={realizedPct}
          />
          <KpiCard
            icon={<MdHistory className="h-5 w-5" />}
            label="Mês anterior"
            value={fmtMoney(data.previousMonthValue)}
            sub={data.previousMonthIsReal ? "valor real" : "previsto"}
            badge={data.previousMonthIsReal ? "real" : "prev"}
          />
          <button
            type="button"
            onClick={() => setOnlyPending((v) => !v)}
            className={`group rounded-2xl p-4 text-left ring-1 backdrop-blur transition-all ${
              data.clientsNeedingAttention > 0
                ? "bg-amber-400/15 ring-amber-300/40 hover:bg-amber-400/25"
                : "bg-white/10 ring-white/15 hover:bg-white/15"
            } ${onlyPending ? "ring-2 ring-amber-300" : ""}`}
          >
            <div className="flex items-center justify-between">
              <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                data.clientsNeedingAttention > 0 ? "bg-amber-300/25 text-amber-100" : "bg-white/15 text-white"
              }`}>
                <MdWarningAmber className="h-5 w-5" />
              </span>
              {onlyPending && <span className="text-[10px] font-bold uppercase text-amber-100">filtrando</span>}
            </div>
            <p className="mt-2 text-[11px] font-medium text-white/70">Sem faturamento</p>
            <p className="text-2xl font-extrabold tabular-nums leading-tight">{data.clientsNeedingAttention}</p>
            <p className="text-[11px] text-white/60">
              {onlyPending ? "ver todos" : "clique p/ filtrar"}
            </p>
          </button>
        </div>
      </div>

      {/* ── Lista de clientes ───────────────────────────────────────────────── */}
      <div className="mt-5 flex flex-col gap-3">
        {clients.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-navy-800">
            Nenhum cliente {onlyPending ? "pendente" : "ativo"} neste período.
          </div>
        )}

        {clients.map((client) => (
          <ClientCard
            key={client.clientId}
            client={client}
            open={expanded.has(client.clientId)}
            canManage={canManage}
            canDelete={canDelete}
            loadingBillingId={loadingBillingId}
            onToggle={() => toggle(client.clientId)}
            onPreview={() => setPreviewClient({ id: client.clientId, name: client.clientName })}
            onCompose={() => setComposeClient(client)}
            onProjectReport={(p) => setProjectReport({ id: p.projectId, name: p.projectName })}
            onProjectEdit={(p) =>
              navigate("/admin/project-management", { state: { openProjectId: p.projectId } })
            }
            onBillingEmit={(b) => setInvoiceTarget({ billing: b, clientId: client.clientId, clientName: client.clientName })}
            onBillingReceive={(b) => handleBillingAction("receive", b.billingId)}
            onBillingCancel={(b) => { setCancelTarget(b); setCancelReason(""); }}
            onBillingDelete={(b) => setDeleteTarget(b)}
          />
        ))}
      </div>

      {previewClient && (
        <ReportPreviewModal
          clientId={previewClient.id}
          clientName={previewClient.name}
          onClose={() => setPreviewClient(null)}
        />
      )}
      {projectReport && (
        <ProjectReportModal
          projectId={projectReport.id}
          projectName={projectReport.name}
          month={month}
          year={year}
          onClose={() => setProjectReport(null)}
        />
      )}
      {invoiceTarget && (
        <InvoiceDocModal
          billingId={invoiceTarget.billing.billingId}
          clientId={invoiceTarget.clientId}
          clientName={invoiceTarget.clientName}
          amount={invoiceTarget.billing.amount}
          billingType={undefined}
          monthRef={month}
          yearRef={year}
          onClose={() => setInvoiceTarget(null)}
          onSuccess={fetchOverview}
        />
      )}

      {composeClient && (
        <BillingComposeModal
          clientId={composeClient.clientId}
          clientName={composeClient.clientName}
          projects={composeClient.projects}
          month={month}
          year={year}
          onClose={() => setComposeClient(null)}
          onSuccess={fetchOverview}
        />
      )}

      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-dv-lg dark:bg-navy-800">
            <h3 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">Cancelar OS</h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Cancelar <span className="font-semibold text-navy-700 dark:text-white">OS #{cancelTarget.billingId}</span>{" "}
              ({fmtMoney(cancelTarget.amount)}). Informe o motivo.
            </p>
            <label htmlFor="ov-cancel-reason" className="sr-only">Motivo do cancelamento</label>
            <textarea
              id="ov-cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Motivo do cancelamento (obrigatório)"
              rows={3}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => { setCancelTarget(null); setCancelReason(""); }}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
              >
                Voltar
              </button>
              <button
                disabled={!cancelReason.trim() || loadingBillingId === cancelTarget.billingId}
                onClick={handleCancelConfirm}
                className="rounded-xl bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingBillingId === cancelTarget.billingId ? "Cancelando…" : "Confirmar cancelamento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-dv-lg dark:bg-navy-800">
            <h3 className="mb-2 flex items-center gap-2 text-lg font-bold text-red-600 dark:text-red-400">
              <MdDeleteForever className="h-5 w-5" /> Excluir OS
            </h3>
            <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">
              Excluir <span className="font-semibold text-navy-700 dark:text-white">OS #{deleteTarget.billingId}</span>{" "}
              ({fmtMoney(deleteTarget.amount)})?
            </p>
            <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
              Ação <strong>permanente</strong>. Para manter histórico, use <strong>Cancelar</strong>.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
              >
                Voltar
              </button>
              <button
                disabled={loadingBillingId === deleteTarget.billingId}
                onClick={handleDeleteConfirm}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingBillingId === deleteTarget.billingId ? "Excluindo…" : "Excluir definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ── Hero KPI card ─────────────────────────────────────────────────────────────
const KpiCard: React.FC<{
  icon: JSX.Element;
  label: string;
  value: string;
  sub?: string;
  badge?: string;
  accent?: "emerald";
  progress?: number;
}> = ({ icon, label, value, sub, badge, accent, progress }) => (
  <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur transition-colors hover:bg-white/[0.14]">
    <div className="flex items-center justify-between">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 text-white">{icon}</span>
      {badge && (
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white/90">
          {badge}
        </span>
      )}
    </div>
    <p className="mt-2 text-[11px] font-medium text-white/70">{label}</p>
    <p className={`text-2xl font-extrabold tabular-nums leading-tight ${accent === "emerald" ? "text-emerald-300" : "text-white"}`}>
      {value}
    </p>
    {progress != null && (
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-white/15">
        <div className="h-full rounded-full bg-emerald-300 transition-all duration-700" style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
    )}
    {sub && <p className="mt-1 text-[11px] text-white/60">{sub}</p>}
  </div>
);

// ── Client card ───────────────────────────────────────────────────────────────
const ClientCard: React.FC<{
  client: ClientOverview;
  open: boolean;
  canManage: boolean;
  /** Pode excluir lançamento (`financial.admin`). */
  /** Pode excluir lancamento (financial.admin). */
  canDelete: boolean;
  loadingBillingId: number | null;
  onToggle: () => void;
  onPreview: () => void;
  onCompose: () => void;
  onProjectReport: (p: ProjectOverview) => void;
  onProjectEdit: (p: ProjectOverview) => void;
  onBillingEmit: (b: ClientBillingRef) => void;
  onBillingReceive: (b: ClientBillingRef) => void;
  onBillingCancel: (b: ClientBillingRef) => void;
  onBillingDelete: (b: ClientBillingRef) => void;
}> = ({ client, open, canManage, canDelete, loadingBillingId, onToggle, onPreview, onCompose, onProjectReport, onProjectEdit, onBillingEmit, onBillingReceive, onBillingCancel, onBillingDelete }) => {
  const stage = client.billingStage;
  const accent =
    stage === "pending"
      ? "before:bg-amber-400"
      : stage === "received"
      ? "before:bg-emerald-400"
      : stage === "to_invoice"
      ? "before:bg-dv-blue-500"
      : "before:bg-gray-300";

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-white shadow-dv-md transition-all duration-200 hover:-translate-y-0.5 hover:nina-surface shadow-dv-lg dark:bg-navy-800
        before:absolute before:left-0 before:top-0 before:h-full before:w-1.5 before:content-[''] ${accent} ${
        client.needsAttention ? "border-amber-200 dark:border-amber-500/30" : "border-gray-100 dark:border-white/10"
      }`}
    >
      <div className="flex items-center gap-3 py-3.5 pl-5 pr-4">
        <button onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <MdExpandMore className={`h-6 w-6 shrink-0 text-gray-400 transition-transform duration-200 dark:text-gray-500 ${open ? "rotate-180" : ""}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-[15px] font-bold text-navy-700 dark:text-white">{client.clientName}</span>
              {stage === "pending" && (
                <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  <MdFlag className="h-3 w-3" /> pendente
                </span>
              )}
              {stage === "to_invoice" && (
                <span className="flex items-center gap-1 rounded-full bg-dv-blue-50 px-2 py-0.5 text-[10px] font-bold text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-blue-300">
                  <MdReceiptLong className="h-3 w-3" /> a faturar
                </span>
              )}
              {stage === "received" && (
                <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                  <MdCheckCircle className="h-3 w-3" /> recebido
                </span>
              )}
              {client.hasUnpaidFixedPrice && (
                <span className="rounded-full bg-dv-magenta-100 px-2 py-0.5 text-[10px] font-bold text-dv-magenta-700 dark:bg-dv-magenta-900/40 dark:text-dv-magenta-300">
                  fixo a receber
                </span>
              )}
            </div>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              <MdAccessTime className="h-3.5 w-3.5" /> {fmtHours(client.totalHours)}
              <span className="text-gray-400">·</span>
              mês ant. {fmtMoney(client.previousValue)} {client.previousIsReal ? "(real)" : "(prev)"}
            </p>
            {client.billings.length > 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {client.billings.map((b) => (
                  <span
                    key={b.billingId}
                    title={`${b.phaseName} · ${fmtMoney(b.amount)}`}
                    className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-navy-700 dark:bg-navy-900 dark:text-gray-200"
                  >
                    OS #{b.billingId}
                  </span>
                ))}
              </div>
            )}
          </div>
        </button>

        <div className="hidden items-center gap-6 md:flex">
          <Metric
            label="Previsto"
            value={fmtMoney(
              client.billings.length > 0
                ? client.billings.reduce((sum, b) => sum + b.amount, 0)
                : client.currentForecast
            )}
          />
          <Metric label="Realizado" value={fmtMoney(client.currentReal)} accent="emerald" />
        </div>

        <button
          onClick={onCompose}
          aria-label={`Nova cobrança para ${client.clientName}`}
          title="Criar cobrança com os projetos deste cliente"
          className="dv-sheen flex shrink-0 items-center gap-1.5 rounded-xl bg-dv-gradient px-3.5 py-2 text-xs font-bold text-white shadow-dv-glow transition-all active:scale-[.97]"
        >
          <MdReceiptLong className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Nova cobrança</span>
        </button>
      </div>

      <div className={`grid transition-all duration-300 ease-in-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="border-t border-gray-100 px-5 py-3 dark:border-gray-700">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">Projetos</span>
              <button
                type="button"
                onClick={onPreview}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-dv-blue-600 dark:border-white/15 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                <MdMailOutline className="h-3.5 w-3.5" aria-hidden="true" />
                Prévia do report
              </button>
            </div>
            {client.projects.length === 0 ? (
              <p className="py-2 text-sm text-gray-500 dark:text-gray-400">Sem projetos ativos.</p>
            ) : (
              <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
                {client.projects.map((p) => (
                  <ProjectRow
                    key={p.projectId}
                    project={p}
                    canManage={canManage}
                    onReport={() => onProjectReport(p)}
                    onEdit={() => onProjectEdit(p)}
                  />
                ))}
              </div>
            )}

            {client.billings.length > 0 && (
              <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-700">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  Ordens de serviço / cobranças do mês
                </span>
                <div className="mt-1.5 flex flex-col gap-1.5">
                  {client.billings.map((b) => {
                    const isLoading = loadingBillingId === b.billingId;
                    return (
                      <div
                        key={b.billingId}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-navy-900"
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-bold text-navy-700 dark:text-white">OS #{b.billingId}</span>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                              b.phase === 3
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                : b.phase === 2
                                ? "bg-dv-blue-50 text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-blue-300"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                            }`}
                          >
                            {b.phaseName}
                          </span>
                          {b.invoiceNumber && (
                            <span className="text-gray-500 dark:text-gray-400">NF {b.invoiceNumber}</span>
                          )}
                          <span className="font-semibold tabular-nums text-navy-700 dark:text-white">
                            {fmtMoney(b.amount)}
                          </span>
                        </span>

                        <div className="flex flex-wrap gap-1.5">
                          {/* Fase 1 — pendente de NF */}
                          {b.phase === 1 && (
                            <button
                              disabled={isLoading}
                              onClick={() => onBillingEmit(b)}
                              className="rounded-lg bg-dv-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-dv-blue-700 disabled:opacity-50 dark:bg-dv-blue-500"
                            >
                              Emitir NF
                            </button>
                          )}
                          {/* Fase 2 — NF emitida, aguarda recebimento */}
                          {b.phase === 2 && (
                            <button
                              disabled={isLoading}
                              onClick={() => onBillingReceive(b)}
                              className="rounded-lg bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              Marcar recebido
                            </button>
                          )}
                          {/* Cancelar — enquanto não recebida */}
                          {(b.phase === 1 || b.phase === 2) && (
                            <button
                              disabled={isLoading}
                              onClick={() => onBillingCancel(b)}
                              className="rounded-lg bg-red-500 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-red-600 disabled:opacity-50"
                            >
                              Cancelar OS
                            </button>
                          )}
                          {/* Excluir — só Admin */}
                          {canDelete && b.phase !== 3 && (
                            <button
                              disabled={isLoading}
                              onClick={() => onBillingDelete(b)}
                              title="Excluir definitivamente"
                              className="rounded-lg border border-red-200 px-2 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
                            >
                              <MdDeleteForever className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {/* Recebida — sem ações */}
                          {b.phase === 3 && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                              <MdCheckCircle className="h-3.5 w-3.5" /> Recebida
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectRow: React.FC<{
  project: ProjectOverview;
  canManage: boolean;
  onReport: () => void;
  onEdit: () => void;
}> = ({ project, canManage, onReport, onEdit }) => (
  <div className="group py-3">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        {canManage ? (
          <button
            type="button"
            onClick={onEdit}
            title="Abrir o cadastro do projeto"
            className="text-sm font-medium text-navy-700 transition-colors hover:text-dv-blue-600 hover:underline dark:text-white dark:hover:text-dv-cyan-300"
          >
            {project.projectName}
          </button>
        ) : (
          <span className="text-sm font-medium text-navy-700 dark:text-white">{project.projectName}</span>
        )}
        {billingTypeChip(project.billingType)}
        <div className="flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <button type="button" onClick={onReport} aria-label="Report detalhado de horas deste projeto" title="Report detalhado de horas deste projeto"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-dv-blue-600 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-dv-cyan-300">
            <MdBarChart className="h-4 w-4" aria-hidden="true" />
          </button>
          {canManage && (
            <button type="button" onClick={onEdit} aria-label="Abrir o cadastro do projeto" title="Abrir o cadastro do projeto"
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-dv-blue-600 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-dv-cyan-300">
              <MdOpenInNew className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-6">
        <Metric label="Horas" value={fmtHours(project.hoursThisMonth)} small />
        {project.billingType !== 1 && <Metric label="Previsto" value={fmtMoney(project.forecast)} small />}
        <Metric label="Realizado" value={fmtMoney(project.real)} accent={project.isBilled ? "emerald" : undefined} small />
      </div>
    </div>

    {project.fixedPrice && (
      <div className="mt-2 rounded-xl bg-gray-50 p-3 dark:bg-navy-900/40">
        <div className="mb-1 flex items-center justify-between text-xs font-medium text-gray-700 dark:text-gray-300">
          <span>
            {project.fixedPrice.consumedHoursTotal.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}
            {" / "}
            {project.fixedPrice.contractedHours?.toLocaleString("pt-BR") ?? "—"} h contratadas
          </span>
          <span className="flex items-center gap-1">
            <MdSchedule className="h-3.5 w-3.5" />
            fim previsto: {fmtDate(project.fixedPrice.projectedEndDate)}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-navy-700">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              (project.fixedPrice.percentConsumed ?? 0) >= 90 ? "bg-red-500"
                : (project.fixedPrice.percentConsumed ?? 0) >= 70 ? "bg-amber-500" : "bg-dv-blue-500"
            }`}
            style={{ width: `${Math.min(100, project.fixedPrice.percentConsumed ?? 0)}%` }}
          />
        </div>
        <div className="mt-1 flex justify-between text-[11px] text-gray-500 dark:text-gray-400">
          <span>{(project.fixedPrice.percentConsumed ?? 0).toFixed(0)}% consumido</span>
          <span>valor: {fmtMoney(project.fixedPrice.contractedAmount ?? 0)}</span>
        </div>
      </div>
    )}
  </div>
);

const Metric: React.FC<{
  label: string;
  value: string;
  accent?: "emerald";
  small?: boolean;
}> = ({ label, value, accent, small }) => (
  <div className="text-right">
    <p className={`${small ? "text-[10px]" : "text-[11px]"} font-medium text-gray-700 dark:text-gray-400`}>{label}</p>
    <p className={`font-bold tabular-nums ${small ? "text-xs" : "text-sm"} ${
      accent === "emerald" ? "text-emerald-600 dark:text-emerald-400" : "text-navy-700 dark:text-white"
    }`}>
      {value}
    </p>
  </div>
);

export default FinancialOverview;
